import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  ElementNode,
  LexicalEditor,
  LexicalNode,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
  SerializedLexicalNode,
  TextNode,
} from 'lexical';
import * as React from 'react';
import {
  useCallback, useEffect,
  useState,
} from 'react';

import { getSelectedNode } from '../../../utils/getSelectedNode';

// eslint-disable-next-line no-unused-vars
export type FindNodeFunc<T extends SerializedLexicalNode | LexicalNode> = (node: TextNode | ElementNode, selection: RangeSelection | null,) => T | null

export default function useFloatingEditorState<T extends SerializedLexicalNode | LexicalNode>(
  editor: LexicalEditor,
  findNode: FindNodeFunc<T>,
): [activeEditor: LexicalEditor, current: {node: T, anchorEl: HTMLElement} | null, close: () => void] {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [current, setCurrent] = useState<{node: T, anchorEl: HTMLElement} | null>(null);

  const close = React.useCallback(() => {
    setCurrent(null);
  }, []);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const selectedNode = getSelectedNode(selection);
      const currentNode = findNode(selectedNode, selection);
      const selectionAnchor = window.getSelection()?.focusNode?.parentElement ?? null;
      if (currentNode && selectionAnchor) {
        setCurrent({
          node: currentNode,
          anchorEl: selectionAnchor,
        });
        return;
      }
      setCurrent(null);
    }
  }, []);

  useEffect(() => mergeRegister(
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    }),
    editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        updateToolbar();
        setActiveEditor(newEditor);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    ),
  ), [editor, updateToolbar]);

  return [activeEditor, current, close];
}
